import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { DragDropContext, Draggable, Droppable, DropResult, DroppableProps } from 'react-beautiful-dnd';
import { Application } from '@feathersjs/feathers';
import { DataDetailToolbar, LoadingSpinner } from '../../components';
import { useFeathers } from '../../app/util';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import type { Election } from '../../app/feathers/elections/Election';
import type { ElectionImplementation } from '../../types/elections/ElectionImplementation';
import {
  LOCAL_MEASURES_CATEGORY,
  STATEWIDE_MEASURES_CATEGORY,
  mergeBallotCategoryOrder,
  mergeStatewideMeasureOrder,
} from './office-order';

interface BallotCategoryRow {
  key: string;
  name: string;
  count: number;
  type: 'office' | 'measures';
  subtitle?: string;
}

interface OfficeRecord {
  key: string;
  name?: string;
}

interface OfficeFindResult {
  data?: OfficeRecord[];
}

interface MeasureOrderRecord {
  _id: string;
  key: string;
  title: string;
}

interface MeasureFindResult {
  total?: number;
  data?: MeasureOrderRecord[];
}

interface OfficeRowProps {
  $isDragging?: boolean;
}

const Wrapper = styled.div`
  width: calc(100% - 2 * 24px);
  display: flex;
  flex-direction: column;
  padding: 24px;
  align-items: stretch;
`;

const Content = styled.div`
  max-width: 760px;
  width: 100%;
  margin-top: 24px;
`;

const LoadingTile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;
`;

const OfficeList = styled.div`
  border-top: solid 1px #eeeeee;
`;

const CategoryItem = styled.div``;

const OfficeRow = styled.div<OfficeRowProps>`
  display: grid;
  grid-template-columns: 40px 1fr 120px;
  align-items: center;
  gap: 12px;
  padding: 14px 12px;
  border-bottom: solid 1px #eeeeee;
  background: ${props => props.$isDragging ? '#fbfbfb' : 'white'};
  border-radius: ${props => props.$isDragging ? '8px' : '0'};
`;

const CategoryTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const MeasureList = styled.div`
  background: #fafafa;
`;

const MeasureRow = styled.div<OfficeRowProps>`
  display: grid;
  grid-template-columns: 40px 1fr;
  align-items: center;
  gap: 12px;
  padding: 10px 12px 10px 52px;
  border-bottom: solid 1px #eeeeee;
  background: ${props => props.$isDragging ? '#f5f5f5' : '#fafafa'};
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #878787;
`;

const CountLabel = styled.div`
  width: 120px;
  text-align: left;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
`;

const chunk = <T,>(values: T[], size: number): T[][] => Array.from(
  { length: Math.ceil(values.length / size) },
  (_, index) => values.slice(index * size, (index + 1) * size)
);

const moveItem = <T,>(items: T[], sourceIndex: number, destinationIndex: number): T[] => {
  const next = items.slice();
  const [removed] = next.splice(sourceIndex, 1);
  next.splice(destinationIndex, 0, removed);
  return next;
};

const StrictModeDroppable = ({ children, ...props }: DroppableProps) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animationFrame = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animationFrame);
      setEnabled(false);
    };
  }, []);

  if (!enabled) return null;

  return (
    <Droppable {...props}>
      {children}
    </Droppable>
  );
};

const RacesReorder = () => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const history = useHistory();
  const { key: electionKey } = useParams<{ key: string }>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<BallotCategoryRow[]>([]);
  const [originalCategoryOrder, setOriginalCategoryOrder] = useState<string[]>([]);
  const [statewideMeasures, setStatewideMeasures] = useState<MeasureOrderRecord[]>([]);
  const [originalStatewideMeasureOrder, setOriginalStatewideMeasureOrder] = useState<string[]>([]);
  const [statewideExpanded, setStatewideExpanded] = useState(false);

  const currentCategoryOrder = useMemo(() => categories.map(category => category.key), [categories]);
  const currentStatewideMeasureOrder = useMemo(
    () => statewideMeasures.map(measure => measure.key),
    [statewideMeasures]
  );
  const alphabeticalStatewideMeasureOrder = useMemo(
    () => statewideMeasures.slice()
      .sort((left, right) => (
        left.title.toLowerCase().localeCompare(right.title.toLowerCase()) ||
        left.key.localeCompare(right.key)
      ))
      .map(measure => measure.key),
    [statewideMeasures]
  );
  const statewideHasCustomOrder = (
    currentStatewideMeasureOrder.join('|') !== alphabeticalStatewideMeasureOrder.join('|')
  );
  const hasChanges = (
    originalCategoryOrder.join('|') !== currentCategoryOrder.join('|') ||
    originalStatewideMeasureOrder.join('|') !== currentStatewideMeasureOrder.join('|')
  );

  const loadData = useCallback(async () => {
    if (!feathers) return;

    setLoading(true);
    setError(null);
    try {
      const [ei, election] = await Promise.all([
        feathers.getService('election-implementations').get(electionKey) as Promise<ElectionImplementation>,
        feathers.getService('elections').get(electionKey) as Promise<Election>,
      ]);
      const officesPresent = ei?.aggregatedInformation?.officesPresent || [];
      let measureCounts = ei?.aggregatedInformation?.measureCounts;
      if (!measureCounts) {
        const [statewideResult, localResult] = await Promise.all([
          feathers.getService('measures').find({
            query: { election: electionKey, district: election.state, $limit: 0 }
          }) as unknown as Promise<MeasureFindResult>,
          feathers.getService('measures').find({
            query: { election: electionKey, district: { $ne: election.state }, $limit: 0 }
          }) as unknown as Promise<MeasureFindResult>,
        ]);
        measureCounts = {
          statewide: statewideResult.total || 0,
          local: localResult.total || 0,
        };
      }
      const resolvedMeasureCounts = measureCounts || {};
      const statewideMeasureRecords: MeasureOrderRecord[] = [];
      let measureSkip = 0;
      while (measureSkip < (resolvedMeasureCounts.statewide || 0)) {
        const result = await feathers.getService('measures').find({
          query: {
            election: electionKey,
            district: election.state,
            $limit: 50,
            $skip: measureSkip,
          }
        }) as unknown as MeasureFindResult;
        const page = result.data || [];
        statewideMeasureRecords.push(...page);
        if (page.length === 0) break;
        measureSkip += page.length;
      }
      const alphabeticalStatewideMeasures = statewideMeasureRecords.slice().sort((left, right) => (
        left.title.toLowerCase().localeCompare(right.title.toLowerCase()) ||
        left.key.localeCompare(right.key)
      ));
      const statewideMeasureKeys = mergeStatewideMeasureOrder(
        election.statewideMeasureOrder,
        alphabeticalStatewideMeasures.map(measure => measure.key)
      );
      const statewideMeasuresByKey = new Map(
        alphabeticalStatewideMeasures.map(measure => [measure.key, measure])
      );
      const orderedStatewideMeasures = statewideMeasureKeys
        .map(key => statewideMeasuresByKey.get(key))
        .filter((measure): measure is MeasureOrderRecord => Boolean(measure));

      const measureCategoryKeys = [
        ...(resolvedMeasureCounts.statewide ? [STATEWIDE_MEASURES_CATEGORY] : []),
        ...(resolvedMeasureCounts.local ? [LOCAL_MEASURES_CATEGORY] : []),
      ];
      const categoryKeys = mergeBallotCategoryOrder(
        election.ballotCategoryOrder || election.raceOfficeOrder,
        [...officesPresent.map(office => office.key), ...measureCategoryKeys]
      );
      const officeCountsByKey = officesPresent.reduce<Record<string, number>>((acc, office) => ({
        ...acc,
        [office.key]: office.count,
      }), {});

      const officeKeys = categoryKeys.filter(key => (
        key !== STATEWIDE_MEASURES_CATEGORY && key !== LOCAL_MEASURES_CATEGORY
      ));
      const officeResults: OfficeRecord[] = officeKeys.length === 0
        ? []
        : (await Promise.all(
            chunk(officeKeys, 19).map(keys => feathers.getService('offices').find({
              query: { key: keys, $limit: keys.length }
            }) as unknown as Promise<OfficeFindResult>)
          )).map(result => result.data || []).flat(1);

      const officesByKey = officeResults.reduce<Record<string, OfficeRecord>>((acc, office) => ({
        ...acc,
        [office.key]: office,
      }), {});

      setOriginalCategoryOrder(categoryKeys);
      setOriginalStatewideMeasureOrder(statewideMeasureKeys);
      setStatewideMeasures(orderedStatewideMeasures);
      setCategories(categoryKeys.map(key => {
        if (key === STATEWIDE_MEASURES_CATEGORY) {
          return {
            key,
            name: 'Statewide Measures',
            count: resolvedMeasureCounts.statewide || 0,
            type: 'measures',
            subtitle: 'Alphabetical within category',
          };
        }
        if (key === LOCAL_MEASURES_CATEGORY) {
          return {
            key,
            name: 'Local Measures',
            count: resolvedMeasureCounts.local || 0,
            type: 'measures',
            subtitle: 'Alphabetical within category',
          };
        }
        return {
          key,
          name: officesByKey[key]?.name || key,
          count: officeCountsByKey[key] || 0,
          type: 'office',
        };
      }));
    } catch (err) {
      console.error('Error loading ballot categories for race reorder:', err);
      setError('Unable to load races and measures for this election.');
    } finally {
      setLoading(false);
    }
  }, [feathers, electionKey]);

  useEffect(() => {
    if (feathers) loadData();
  }, [feathers, loadData]);

  const handleDragEnd = (result: DropResult) => {
    if (result.reason !== 'DROP' || !result.destination) return;
    if (result.source.droppableId === 'statewide-measure-order') {
      if (result.destination.droppableId !== 'statewide-measure-order') return;
      setStatewideMeasures(moveItem(
        statewideMeasures,
        result.source.index,
        result.destination.index
      ));
      return;
    }
    if (result.destination.droppableId === 'statewide-measure-order') return;
    setCategories(moveItem(categories, result.source.index, result.destination.index));
  };

  const handleSave = async () => {
    if (!feathers) return;

    setSaving(true);
    setError(null);
    try {
      await feathers.getService('election-implementations').patch(electionKey, {
        $reorderRaces: true,
        categoryOrder: currentCategoryOrder,
        statewideMeasureOrder: currentStatewideMeasureOrder,
      });
      history.push(`/elections/${electionKey}/races`);
    } catch (err) {
      console.error('Error saving race order:', err);
      setError(err instanceof Error ? err.message : 'Unable to save race order. Please try again.');
      setSaving(false);
    }
  };

  return (
    <Wrapper>
      <DataDetailToolbar
        onBack={{ to: { pathname: `/elections/${electionKey}/races` }}}
        navTree={[
          {
            text: 'Elections',
            to: { pathname: '/elections' }
          },
          {
            text: electionKey,
            to: { pathname: `/elections/${electionKey}` }
          },
          {
            text: 'Races',
            to: { pathname: `/elections/${electionKey}/races` }
          },
          { text: 'Reorder' }
        ]}
      />
      <Content>
        <Typography variant="h4" color="primary">Reorder races</Typography>
        <Typography variant="body1" color="textSecondary" style={{ marginTop: 8, marginBottom: 24 }}>
          Drag offices and measure categories into the order they should appear on the ballot. Races within each office will be ordered by district identifier.
        </Typography>
        {error && <Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>}
        {loading ? (
          <LoadingTile>
            <LoadingSpinner width={32} />
          </LoadingTile>
        ) : categories.length === 0 ? (
          <Typography variant="body1" color="primary">No races or measures found for this election.</Typography>
        ) : (
          <>
            <DragDropContext onDragEnd={handleDragEnd}>
              <StrictModeDroppable
                droppableId="race-office-order"
                type="race-office"
              >
                {(provided) => (
                  <OfficeList ref={provided.innerRef} {...provided.droppableProps}>
                    {categories.map((category, index) => (
                      <Draggable key={category.key} draggableId={category.key} index={index}>
                        {(dragProvided, snapshot) => (
                          <CategoryItem
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                          >
                            <OfficeRow $isDragging={snapshot.isDragging}>
                              <DragHandle {...dragProvided.dragHandleProps}>
                                <DragIndicatorIcon />
                              </DragHandle>
                              <div>
                                <CategoryTitle>
                                  <Typography variant="body1" color="primary" style={{ fontWeight: 'bold' }}>
                                    {category.name}
                                  </Typography>
                                  {category.key === STATEWIDE_MEASURES_CATEGORY && (
                                    <IconButton
                                      size="small"
                                      aria-label={statewideExpanded ? 'Collapse statewide measures' : 'Expand statewide measures'}
                                      onClick={() => setStatewideExpanded(expanded => !expanded)}
                                    >
                                      {statewideExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                  )}
                                </CategoryTitle>
                                {category.subtitle && (
                                  <Typography variant="body2" color="textSecondary">
                                    {category.key === STATEWIDE_MEASURES_CATEGORY && statewideHasCustomOrder
                                      ? 'Custom order'
                                      : category.subtitle}
                                  </Typography>
                                )}
                              </div>
                              <CountLabel>
                                <Typography variant="body1" color="primary">
                                  {category.count} {category.type === 'measures'
                                    ? (category.count === 1 ? 'measure' : 'measures')
                                    : (category.count === 1 ? 'race' : 'races')}
                                </Typography>
                              </CountLabel>
                            </OfficeRow>
                            {category.key === STATEWIDE_MEASURES_CATEGORY && statewideExpanded && (
                              <StrictModeDroppable
                                droppableId="statewide-measure-order"
                                type="statewide-measure"
                              >
                                {(measureProvided) => (
                                  <MeasureList
                                    ref={measureProvided.innerRef}
                                    {...measureProvided.droppableProps}
                                  >
                                    {statewideMeasures.map((measure, measureIndex) => (
                                      <Draggable
                                        key={measure.key}
                                        draggableId={`statewide-measure:${measure.key}`}
                                        index={measureIndex}
                                      >
                                        {(measureDragProvided, measureSnapshot) => (
                                          <MeasureRow
                                            ref={measureDragProvided.innerRef}
                                            {...measureDragProvided.draggableProps}
                                            $isDragging={measureSnapshot.isDragging}
                                          >
                                            <DragHandle {...measureDragProvided.dragHandleProps}>
                                              <DragIndicatorIcon fontSize="small" />
                                            </DragHandle>
                                            <Typography variant="body2" color="primary">
                                              {measure.title}
                                            </Typography>
                                          </MeasureRow>
                                        )}
                                      </Draggable>
                                    ))}
                                    {measureProvided.placeholder}
                                  </MeasureList>
                                )}
                              </StrictModeDroppable>
                            )}
                          </CategoryItem>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </OfficeList>
                )}
              </StrictModeDroppable>
            </DragDropContext>
            <Actions>
              <Button
                variant="outlined"
                onClick={() => history.push(`/elections/${electionKey}/races`)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={saving || !hasChanges}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Actions>
          </>
        )}
      </Content>
    </Wrapper>
  );
};

export default RacesReorder;
